import { MarsSsoCcSdk } from "../../Shared/Assets/src/types/mars-sso-cc-sdk";
import {
  ConsentErrorKind,
  clearOneTrustValidatorMessage,
  getConsentErrorMessage,
  setSubmissionErrorVisible
} from "./errors";
import {
  analyticsCollectionPoint,
  buildConsentCaptureForm,
  getConsentCaptureSignature
} from "./field-capture";
import { predictFormsSubmit } from "./forms-parity";
import {
  CaptureReason,
  PayloadSummary,
  describePayload,
  trackConsentCapture
} from "./telemetry";
import { areRequiredConsentCheckboxesChecked, hasValidContactInfo } from "./validity";

const CONSENT_CAPTURE_TIMEOUT_MS = 10_000;

// Signature (see getConsentCaptureSignature) of the last payload successfully captured per form,
// for the page's lifetime. The OneTrust consent API isn't idempotent, and some failures happen
// after this hook where no prediction can see them (server-side reCAPTCHA/validation rejecting
// the Submit, the Submit XHR failing, the summarised-data confirm being cancelled) - a resubmit
// with unchanged data must not record the same consent twice. Changed data is a different
// consent record, so it's still sent.
const lastCapturedSignatures = new Map<string, string>();

// The capture call still running per form, if any. A call that outlives our timeout keeps running
// (and may still succeed), and the timeout error invites a retry - which, with unchanged data,
// waits on that call again instead of sending the same consent a second time.
const inFlightCaptures = new Map<string, { signature: string; capture: Promise<void> }>();

// Whether the latest submit attempt's own payload has reached OneTrust, per form. Every Forms
// submit runs onBeforeSubmit first, so for a Submit outcome this is that submission's attempt -
// unlike lastCapturedSignatures, which stays set after an attempt that skipped capture.
const submissionCaptured = new Map<string, boolean>();

class ConsentCaptureTimeoutError extends Error {
  constructor() {
    super("OneTrust consent capture timed out");
  }
}

// Picks the message for a failed capture call. The SDK rethrows the consent API's validation
// messages (e.g. "The user's email address is malformed") as the error's message, joining them
// when there are several, without the field they relate to - so they're matched loosely, and only
// against a field this attempt actually sent: a visitor who entered just a phone number is never
// asked to check an email address. Anything else gets the general message, which is still accurate.
const getCaptureFailureKind = (
  error: unknown,
  contactSent: PayloadSummary["contactSent"]
): ConsentErrorKind => {
  if (error instanceof ConsentCaptureTimeoutError || navigator.onLine === false) {
    return "connection";
  }

  const message = error instanceof Error ? error.message : String(error);

  if (/network error|timeout|timed out/i.test(message)) {
    return "connection";
  }

  const sentEmail = contactSent === "EmailAndPhone" || contactSent === "EmailOnly";
  const sentPhone = contactSent === "EmailAndPhone" || contactSent === "PhoneOnly";

  if (sentEmail && /e-?mail/i.test(message)) {
    return "email";
  }

  if (sentPhone && /phone/i.test(message)) {
    return "phone";
  }

  return "save";
};

const wait = (ms: number): Promise<never> =>
  new Promise((_, reject) => {
    setTimeout(() => reject(new ConsentCaptureTimeoutError()), ms);
  });

const warnSkipped = (form: HTMLFormElement, reason: string): void => {
  console.warn(`OneTrust consent capture skipped for form "${form.id}": ${reason}`);
};

// Invoked from EPiServer.Forms.Extension.onBeforeSubmit (registered in initializeOneTrustConsent),
// which Forms awaits *before* validating the current step and finalizing/navigating - unlike
// formsSubmitted, which fires after finalization and so cannot reliably prevent the browser from
// cancelling an in-flight consent capture request when the page navigates away.
//
// Resolves without capturing when there is no OneTrust touchpoint in this form (no behavior change
// for non-consent forms), when this isn't the finalising submit (a multi-step "Next"), when Forms
// is about to reject the step itself (it shows its own errors; nothing has been sent yet, so
// nothing is duplicated when the visitor corrects them and resubmits), or when this exact payload
// was already captured. Otherwise resolves once consent capture succeeds (waiting on the earlier
// call instead when this exact payload is still being sent), and rejects on failure/timeout (10s)
// after showing a visible, retry-capable error.
export const submitOneTrustConsentCapture = async (
  workingFormElement: HTMLElement | null,
  marsSsoCcSdk: MarsSsoCcSdk,
  submitAction: HTMLElement | undefined
): Promise<void> => {
  const marker = workingFormElement?.querySelector<HTMLElement>(
    "[data-ssocc-touchpoint]"
  );

  if (!marker || !(workingFormElement instanceof HTMLFormElement)) {
    return;
  }

  submissionCaptured.set(workingFormElement.id, false);

  const prediction = predictFormsSubmit(workingFormElement, submitAction);

  if (prediction.kind === "not-final") {
    return;
  }

  // Taken now, while oneTrustFieldValues is known to hold this attempt's payload - see
  // describePayload.
  const payload = describePayload(marker);

  if (prediction.kind === "rejected") {
    trackConsentCapture(workingFormElement, {
      outcome: "Skipped",
      reason: "FormsRejected",
      failedValidators: prediction.failedValidators,
      payload
    });
    return;
  }

  const predictionTelemetry =
    prediction.kind === "unknown"
      ? { prediction: "Unknown" as const, predictionReason: prediction.reason }
      : { prediction: "Accepted" as const };

  if (prediction.kind === "unknown") {
    // Can't tell whether Forms will submit, so capture anyway: a possible duplicate consent
    // record is the lesser problem compared to a submission whose consent is never captured.
    console.warn(
      `OneTrust consent capture can't predict EPiServer Forms' submit decision (${prediction.reason}); capturing anyway.`
    );
  }

  const skip = (reason: CaptureReason, description: string): void => {
    warnSkipped(workingFormElement, description);
    trackConsentCapture(workingFormElement, {
      outcome: "Skipped",
      reason,
      ...predictionTelemetry,
      payload
    });
  };

  // Forms is about to accept the submission, so these only fail where Forms deliberately doesn't
  // validate the consent element, i.e. it's hidden by a field dependency - the visitor hasn't
  // been asked for consent, so there's nothing to capture.
  if (!areRequiredConsentCheckboxesChecked(marker)) {
    skip("ConsentNotGiven", "required consent checkboxes aren't checked");
    return;
  }

  // One capture call sends every touchpoint's purposes in the form, so it has to meet all of
  // their contact requirements.
  const markers = Array.from(
    workingFormElement.querySelectorAll<HTMLElement>("[data-ssocc-touchpoint]")
  );

  if (!markers.every(m => hasValidContactInfo(workingFormElement, m))) {
    skip("NoValidContact", "the contact details the touchpoint requires aren't valid");
    return;
  }

  const signature = getConsentCaptureSignature();

  if (lastCapturedSignatures.get(workingFormElement.id) === signature) {
    // Forms is about to submit, and this payload has already reached OneTrust.
    submissionCaptured.set(workingFormElement.id, true);
    skip("AlreadyCaptured", "this consent was already captured");
    return;
  }

  // Reached only once the fields are confirmed valid above, so any validator message still shown
  // from an earlier, since-corrected submit attempt (Forms can't have cleared it itself yet - see
  // clearOneTrustValidatorMessage) is now stale and would otherwise sit alongside our own
  // submission error below, misleadingly implying the fields are still invalid.
  clearOneTrustValidatorMessage(marker);
  setSubmissionErrorVisible(marker, false);

  const formId = workingFormElement.id;
  const startedAt = performance.now();
  const inFlight = inFlightCaptures.get(formId);
  const joinedInFlight = inFlight?.signature === signature;
  let timedOut = false;

  const startCapture = (): Promise<void> => {
    // Recorded from the SDK call itself rather than after the race below, so a call that
    // succeeds only after our timeout has already shown an error still counts as captured.
    const started = marsSsoCcSdk
      .submitConsentCapture(
        buildConsentCaptureForm(),
        marsSsoCcSdk.consentMutations.updateConsent,
        { analyticsCollectionPoint }
      )
      .then(() => {
        lastCapturedSignatures.set(formId, signature);
        trackConsentCapture(workingFormElement, {
          outcome: "Captured",
          ...predictionTelemetry,
          durationMs: performance.now() - startedAt,
          capturedAfterTimeout: timedOut,
          payload
        });
      });

    inFlightCaptures.set(formId, { signature, capture: started });

    // Bookkeeping only - the outcome is handled by whichever attempt is awaiting the call.
    started
      .catch(() => undefined)
      .then(() => {
        if (inFlightCaptures.get(formId)?.capture === started) {
          inFlightCaptures.delete(formId);
        }
      });

    return started;
  };

  const capture = joinedInFlight ? inFlight.capture : startCapture();

  try {
    await Promise.race([capture, wait(CONSENT_CAPTURE_TIMEOUT_MS)]);
    submissionCaptured.set(formId, true);

    if (joinedInFlight) {
      // The call waited on here reports its own Captured event.
      trackConsentCapture(workingFormElement, {
        outcome: "Skipped",
        reason: "JoinedInFlight",
        ...predictionTelemetry,
        durationMs: performance.now() - startedAt,
        payload
      });
    }
  } catch (e) {
    timedOut = e instanceof ConsentCaptureTimeoutError;

    const errorKind = getCaptureFailureKind(e, payload.contactSent);

    trackConsentCapture(workingFormElement, {
      outcome: "Failed",
      reason: timedOut ? "Timeout" : "ApiError",
      ...predictionTelemetry,
      durationMs: performance.now() - startedAt,
      error: timedOut ? undefined : e,
      errorMessageShown: errorKind,
      payload
    });

    console.warn(e);
    setSubmissionErrorVisible(marker, true, getConsentErrorMessage(marker, errorKind));
    throw e;
  }
};

// Whether consent has been captured for this form on this page, for OneTrustFormSubmitted.
export const hasCapturedConsent = (formId: string): boolean =>
  lastCapturedSignatures.has(formId);

// Whether the latest submit attempt's own payload reached OneTrust, for OneTrustFormSubmitted.
export const hasCapturedSubmissionConsent = (formId: string): boolean =>
  submissionCaptured.get(formId) ?? false;
